import chatService from "../services/chatService";
import { buildSortedSQL } from "./buildSortedSQL";
import { parseExecuteSqlResponse } from "./parseExecuteSqlResponse";
import {
  deriveTableHasMoreVisible,
  resolveSortExecuteLimit,
  resolveTotalRowCount,
} from "./tablePaginationUtils";

/**
 * First-page table sort via POST /api/query/execute-sql (not /api/query/sort).
 *
 * Wraps base_sql: SELECT * FROM (<base_sql>) AS t ORDER BY `col` ASC|DESC [LIMIT n]
 * - No user limit: offset 0, limit 100 (TABLE_PAGE_SIZE); no LIMIT in SQL.
 * - User limit n: offset 0, limit n; SQL includes LIMIT n (same n in body).
 * Load-more after sort uses stored sorted_sql with offset 100, 200, … (batch 100).
 */
export async function fetchSortedTableViaExecuteSql({
  baseSql,
  columnName,
  sortOrder,
  userLimit = null,
  knownTotalCount = null,
  knownLoadedCount = 0,
  sessionId,
  userId = null,
  referenceRows = null,
}) {
  if (!baseSql || !columnName || !sortOrder) {
    return { ok: false, sortedSql: null, response: null };
  }

  const requestLimit = Math.max(
    1,
    resolveSortExecuteLimit(userLimit, knownTotalCount, knownLoadedCount)
  );
  const sqlRowLimit =
    userLimit != null && userLimit !== "" && Number(userLimit) > 0
      ? requestLimit
      : null;
  const sortedSql = buildSortedSQL(baseSql, columnName, sortOrder, sqlRowLimit);

  const apiResponse = await chatService.executeSql(
    sortedSql,
    0,
    requestLimit,
    sessionId,
    userId
  );

  const parsed = parseExecuteSqlResponse(apiResponse, referenceRows);
  const resultLoadedCount = parsed.rows.length;
  const resultTotalCount = resolveTotalRowCount(
    parsed.dataSummary,
    parsed.pagination,
    knownTotalCount,
    resultLoadedCount
  );
  const hasMore = deriveTableHasMoreVisible({
    displayedCount: resultLoadedCount,
    totalCount: resultTotalCount,
    paginationInfo: parsed.pagination,
    dataSummary: parsed.dataSummary,
  });

  const response = {
    success: parsed.ok,
    data: parsed.rows,
    total_count: resultTotalCount,
    totalCount: resultTotalCount,
    has_more: hasMore,
    hasMore,
    page_size: requestLimit,
    pageSize: requestLimit,
    sorted_sql: sortedSql,
    sortedSql,
  };

  return { ok: parsed.ok, sortedSql, response, rows: parsed.rows };
}
